import type { ReactNode } from "react";

import { FormattedMessage } from "react-intl";

import { ComboboxField } from "@ps-design-system/components/Combobox";
import { InputField } from "@ps-design-system/components/Input";

import type { SelectOption } from "../app-main/types/general.types";

import type { AddressFieldNames, Country } from "./AddressForm.types";

type AddressFieldsProps = {
  /** Locks the country field for an account whose country is already set. */
  countryDisabled: boolean;
  /** Overrides the plain "Country" label, e.g. to attach a tooltip. */
  countryLabel?: ReactNode;
  /** Field labels in the selected country's own wording, from `@shopify/address`. */
  countryLabels?: Country["labels"];
  countryOptions: SelectOption[];
  /** What this address's fields are called in the consumer's form values. */
  names: AddressFieldNames;
  /**
   * Wraps the street label, e.g. to attach a tooltip, without the caller having to
   * work out what the label says — it is handed the resolved one.
   */
  renderStreetLabel?: (label: ReactNode) => ReactNode;
  testIdPrefix: string;
  zoneOptions: SelectOption[];
};

/**
 * One postal address, rendered wherever an address is collected. Countries with no
 * zones get no zone field, the same condition under which `createAddressRules`
 * stops requiring one.
 */
const AddressFields = ({
  countryDisabled,
  countryLabel,
  countryLabels,
  countryOptions,
  names,
  renderStreetLabel,
  testIdPrefix,
  zoneOptions,
}: AddressFieldsProps) => {
  const streetLabel = countryLabels?.address1 ?? (
    <FormattedMessage
      id="hosting.address_form.street_address_label"
      defaultMessage="Street address"
    />
  );

  return (
    <div className="tw-grid tw-gap-24 sm:tw-grid-cols-2">
      <ComboboxField
        disabled={countryDisabled}
        fullWidth={true}
        label={
          countryLabel ?? (
            <FormattedMessage
              id="hosting.address_form.country_label"
              defaultMessage="Country"
            />
          )
        }
        name={names.country}
        options={countryOptions}
        // A locked country cannot be changed, so marking it required is noise.
        required={!countryDisabled}
        testId={`${testIdPrefix}-country`}
        // The trigger lays its content out with `justify-between`, so with nothing
        // selected the chevron is the only child and drifts to the left. A
        // placeholder keeps it pinned right whether or not a value is set.
        triggerProps={{
          placeholder: (
            <FormattedMessage
              id="hosting.address_form.country_label"
              defaultMessage="Country"
            />
          ),
        }}
      />

      {/* Keeps country alone on its row so the street field starts the next one. */}
      <div aria-hidden={true} className="tw-hidden sm:tw-block" />

      <InputField
        fullWidth={true}
        label={renderStreetLabel ? renderStreetLabel(streetLabel) : streetLabel}
        name={names.street}
        required={true}
        testId={`${testIdPrefix}-street`}
      />

      <InputField
        fullWidth={true}
        label={
          countryLabels?.address2 ?? (
            <FormattedMessage
              id="hosting.address_form.address_2_label"
              defaultMessage="Address 2"
            />
          )
        }
        name={names.unit}
        testId={`${testIdPrefix}-unit`}
      />

      <InputField
        fullWidth={true}
        label={
          countryLabels?.city ?? (
            <FormattedMessage
              id="hosting.address_form.city_label"
              defaultMessage="City"
            />
          )
        }
        name={names.city}
        required={true}
        testId={`${testIdPrefix}-city`}
      />

      {zoneOptions.length > 0 && (
        <ComboboxField
          fullWidth={true}
          label={countryLabels?.zone}
          name={names.zone}
          options={zoneOptions}
          required={true}
          testId={`${testIdPrefix}-state`}
          triggerProps={{ placeholder: countryLabels?.zone }}
        />
      )}

      <InputField
        fullWidth={true}
        label={
          countryLabels?.postalCode ?? (
            <FormattedMessage
              id="hosting.address_form.postal_code"
              defaultMessage="Postal Code"
            />
          )
        }
        name={names.postalCode}
        required={true}
        testId={`${testIdPrefix}-zip`}
      />
    </div>
  );
};

export { AddressFields };

export type { AddressFieldsProps };
